/**
 * useCharlotteVoice — Reusable Voice Hook for Charlotte-powered Assistants
 *
 * Manages a Gemini Live WebSocket session with bidirectional PCM audio streaming.
 * Captures mic audio, resamples to 24 kHz PCM16, streams to Charlotte backend.
 * Receives audio responses and plays them via AudioContext queue.
 *
 * Usage:
 *   const voice = useCharlotteVoice({
 *     apiUrl: 'https://charlotte.netrunsystems.com:8080',
 *     ragCollection: 'kog_sales',
 *     instructions: 'You are a sales assistant...',
 *   });
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type {
  CharlotteConfig,
  CharlotteTranscript,
  CharlotteStats,
  CharlotteEngagement,
  RagContextEvent,
} from '../types';

export interface UseCharlotteVoiceOptions extends CharlotteConfig {
  onTranscript?: (t: CharlotteTranscript) => void;
  onRagContext?: (e: RagContextEvent) => void;
  onError?: (msg: string) => void;
  onEngagement?: (e: CharlotteEngagement) => void;
}

export interface UseCharlotteVoiceReturn {
  isConnected: boolean;
  isActive: boolean;
  isMuted: boolean;
  isSpeaking: boolean;
  transcripts: CharlotteTranscript[];
  stats: CharlotteStats;
  engagement: CharlotteEngagement | null;
  start: () => Promise<void>;
  stop: () => void;
  toggleMute: () => void;
  cancel: () => void;
  clearTranscripts: () => void;
  sendText: (text: string) => void;
}

function resampleTo24k(inputBuffer: Float32Array, inputSampleRate: number): Int16Array {
  const ratio = inputSampleRate / 24000;
  const outputLength = Math.floor(inputBuffer.length / ratio);
  const output = new Int16Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const srcIdx = Math.floor(i * ratio);
    const sample = Math.max(-1, Math.min(1, inputBuffer[srcIdx]));
    output[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return output;
}

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function useCharlotteVoice(options: UseCharlotteVoiceOptions): UseCharlotteVoiceReturn {
  const {
    apiUrl,
    wsUrl: wsUrlOverride,
    voice,
    instructions,
    ragCollection,
    onTranscript,
    onRagContext,
    onError,
    onEngagement,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcripts, setTranscripts] = useState<CharlotteTranscript[]>([]);
  const [stats, setStats] = useState<CharlotteStats>({
    audioChunksSent: 0,
    audioChunksReceived: 0,
    responsesCount: 0,
  });
  const [engagement, setEngagement] = useState<CharlotteEngagement | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const isMutedRef = useRef(false);
  const audioQueueRef = useRef<ArrayBuffer[]>([]);
  const isPlayingRef = useRef(false);

  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);

  const playNextChunk = useCallback(async () => {
    if (!audioCtxRef.current || audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      return;
    }
    isPlayingRef.current = true;
    const chunk = audioQueueRef.current.shift()!;
    const pcm16 = new Int16Array(chunk);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / (pcm16[i] < 0 ? 0x8000 : 0x7fff);
    }
    const buffer = audioCtxRef.current.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32, 0);
    const source = audioCtxRef.current.createBufferSource();
    source.buffer = buffer;
    source.connect(audioCtxRef.current.destination);
    source.onended = () => playNextChunk();
    source.start();
  }, []);

  const enqueueAudio = useCallback((base64Audio: string) => {
    const binary = atob(base64Audio);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    audioQueueRef.current.push(bytes.buffer);
    if (!isPlayingRef.current) playNextChunk();
  }, [playNextChunk]);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      switch (data.type) {
        case 'session_ready':
          setIsConnected(true);
          break;
        case 'audio':
          if (data.audio) {
            setIsSpeaking(true);
            setStats(prev => ({ ...prev, audioChunksReceived: prev.audioChunksReceived + 1 }));
            enqueueAudio(data.audio);
          }
          break;
        case 'transcript_done': {
          const t: CharlotteTranscript = {
            id: crypto.randomUUID(),
            source: data.source,
            text: data.text,
            timestamp: Date.now(),
          };
          setTranscripts(prev => [...prev, t]);
          onTranscript?.(t);
          break;
        }
        case 'response_done':
          setIsSpeaking(false);
          setStats(prev => ({ ...prev, responsesCount: prev.responsesCount + 1 }));
          break;
        case 'response_cancelled':
          setIsSpeaking(false);
          break;
        case 'reconnecting':
          setIsConnected(false);
          setIsSpeaking(false);
          break;
        case 'rag_context':
          onRagContext?.({
            sources: data.sources || [],
            context_length: data.context_length || 0,
            passive: data.passive,
            queued: data.queued,
            timestamp: Date.now(),
          });
          break;
        case 'engagement_signal': {
          const sig: CharlotteEngagement = {
            signal: data.signal,
            register: data.register,
            satisfaction_total: data.satisfaction_total ?? 0,
            frustration_total: data.frustration_total ?? 0,
            timestamp: Date.now(),
          };
          setEngagement(sig);
          onEngagement?.(sig);
          break;
        }
        case 'error':
          onError?.(data.message || 'Unknown error');
          break;
        case 'heartbeat':
        case 'pong':
          break;
      }
    } catch {
      // Ignore non-JSON messages
    }
  }, [enqueueAudio, onTranscript, onRagContext, onError, onEngagement]);

  const buildWsUrl = useCallback(() => {
    let base = wsUrlOverride;
    if (!base) {
      base = apiUrl.replace(/^http/, 'ws') + '/api/voice/voicelive/ws';
    }
    const params = new URLSearchParams();
    if (voice) params.set('voice', voice);
    if (instructions) params.set('instructions', instructions);
    if (ragCollection) params.set('rag_collection', ragCollection);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }, [apiUrl, wsUrlOverride, voice, instructions, ragCollection]);

  const start = useCallback(async () => {
    if (isActive) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { sampleRate: { ideal: 48000 }, channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });
    micStreamRef.current = stream;
    const audioCtx = new AudioContext({ sampleRate: 48000 });
    audioCtxRef.current = audioCtx;
    const source = audioCtx.createMediaStreamSource(stream);
    const processor = audioCtx.createScriptProcessor(4096, 1, 1);
    processorRef.current = processor;
    const url = buildWsUrl();
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => setIsActive(true);
    ws.onclose = () => { setIsConnected(false); setIsActive(false); setIsSpeaking(false); };
    ws.onerror = () => onError?.('WebSocket connection failed');
    ws.onmessage = handleMessage;
    processor.onaudioprocess = (e: AudioProcessingEvent) => {
      if (isMutedRef.current || ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      const pcm16 = resampleTo24k(input, audioCtx.sampleRate);
      const b64 = int16ToBase64(pcm16);
      ws.send(JSON.stringify({ type: 'audio', audio: b64 }));
      setStats(prev => ({ ...prev, audioChunksSent: prev.audioChunksSent + 1 }));
    };
    source.connect(processor);
    processor.connect(audioCtx.destination);
  }, [isActive, buildWsUrl, handleMessage, onError]);

  const stop = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    micStreamRef.current?.getTracks().forEach(t => t.stop());
    micStreamRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    wsRef.current?.close();
    wsRef.current = null;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsActive(false);
    setIsConnected(false);
    setIsSpeaking(false);
    setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => setIsMuted(prev => !prev), []);

  const cancel = useCallback(() => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsSpeaking(false);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'cancel' }));
    }
  }, []);

  const sendText = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN && text.trim()) {
      wsRef.current.send(JSON.stringify({ type: 'text', text: text.trim() }));
    }
  }, []);

  const clearTranscripts = useCallback(() => setTranscripts([]), []);

  useEffect(() => () => { stop(); }, [stop]);

  return {
    isConnected, isActive, isMuted, isSpeaking,
    transcripts, stats, engagement,
    start, stop, toggleMute, cancel, clearTranscripts, sendText,
  };
}
